import { forwardRef, useState, type MouseEvent, type ReactNode } from "react";

import { Tune } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Popover,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";

// TODO(filters-migration): The following modules still use the legacy
// `SearchBarFilters` / `RenderFilterChips` / `FilterChip` / `DateRangePickerCustom`
// helpers in apps/portal/src/components/alerts/Search/SearchFilters.tsx. Once
// migrated to `@rubi/ui` FiltersButton, delete that file:
//   - apps/portal/src/components/alerts/Search/AlertFilters.tsx
//   - apps/portal/src/components/alerts/AlertsList.tsx
//   - apps/portal/src/components/online-reporting/Search/ReportFilters.tsx
//   - apps/portal/src/components/online-reporting/ReportV2List.tsx
//   - apps/portal/src/components/registry-system/RegistryEntries.tsx
// Plan: lift the four helpers into libs/ui/src/components/FiltersButton/
// (sibling files: FilterChip.tsx, FilterChipsRow.tsx, FilterDateRangePicker.tsx;
// the popover shell collapses into FiltersButton itself). Each consumer then
// imports from `@rubi/ui` and renders fields as `<FiltersButton>` children
// instead of passing `gridElements` to `<SearchBarFilters>`.
//
// Migration friction notes (RubiREPORT, RubiALERT, RubiINTEL, registry-system):
// adoption is NOT a one-line drop-in — expect a small per-module PR for each:
//
//  1. Trigger location moves out of SearchBar.
//     Today the Filters trigger lives inside `SearchBar.tsx` (~line 282) and
//     parents pass `filtersVisible` / `setFiltersVisible` plus a separate
//     `<ReportFilters>` / `<AlertFilters>` render. FiltersButton bundles trigger
//     + popover, so SearchBar must skip its internal Filters button (add a flag
//     or remove it) and the parent renders `<FiltersButton>` alongside SearchBar.
//
//  2. Popover anchoring changes visually.
//     Today's popover anchors to the search-bar container (≤800px, sits flush
//     under SearchBar). FiltersButton anchors to the button itself with a fixed
//     ~640px width opening leftward. Intentional fix for Feedback's "too wide"
//     bug, but Reports/Alert users will see the popover detach from the search
//     bar. If a module needs the old visual, set `width` to match the search-bar
//     width and/or pass a custom `buttonSx` to position the button — but the
//     simpler outcome is to accept the new look.
//
//  3. State ownership flips to internal.
//     FiltersButton owns its own open/close. Anything that drives the panel
//     programmatically (URL deep-links, "open filters when no results", reset
//     buttons elsewhere) needs a controlled-mode escape hatch. NOT implemented
//     today — add optional `open` + `onOpenChange` props before migrating the
//     first module that needs them.

export interface FiltersButtonProps {
  children: ReactNode;
  activeCount?: number;
  label?: string;
  width?: number | string;
  onClear?: () => void;
  clearLabel?: string;
  buttonSx?: SxProps<Theme>;
}

const DEFAULT_WIDTH = 640;

export const FiltersButton = forwardRef<HTMLButtonElement, FiltersButtonProps>(
  function FiltersButton(
    {
      children,
      activeCount = 0,
      label = "Filters",
      width = DEFAULT_WIDTH,
      onClear,
      clearLabel = "Clear Filters",
      buttonSx,
    },
    ref,
  ) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = anchorEl !== null;

    const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    return (
      <>
        <Button
          ref={ref}
          variant="outlined"
          onClick={handleOpen}
          aria-haspopup="dialog"
          aria-expanded={open}
          startIcon={
            <Badge badgeContent={activeCount} color="primary">
              <Tune />
            </Badge>
          }
          sx={{ minWidth: 140, height: 48, ...buttonSx }}
        >
          {label}
        </Button>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          sx={{ mt: 1 }}
          slotProps={{
            paper: {
              sx: {
                p: 2,
                width: { xs: "calc(100vw - 32px)", sm: width },
                maxWidth: "100vw",
              },
            },
          }}
        >
          <Stack spacing={2}>
            <Box>{children}</Box>
            {onClear && (
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                onClick={onClear}
              >
                {clearLabel}
              </Button>
            )}
          </Stack>
        </Popover>
      </>
    );
  },
);
